import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Brain, DollarSign, ArrowRight, Activity, Zap, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsLocalLLM() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const ollamaInstall = `# Install Ollama
brew install ollama

# Start Ollama service
ollama serve

# Pull recommended models
ollama pull mistral:7b    # Intent classification (~4GB)
ollama pull llama3:8b      # Text generation (~4.7GB)

# Verify installation
ollama list`;

  const ollamaHealthCheck = `# Check Ollama health
curl http://localhost:11434/api/tags

# Expected response:
# {
#   "models": [
#     { "name": "mistral:7b", "size": 4109865472 },
#     { "name": "llama3:8b", "size": 4661211648 }
#   ]
# }

# Test a generation
curl http://localhost:11434/api/generate -d '{
  "model": "mistral:7b",
  "prompt": "Classify this message intent: I want to create a 20% off deal",
  "stream": false
}'`;

  const claudeSetup = `# Get your API key from https://console.anthropic.com
# Add to your .env file:
ANTHROPIC_API_KEY=sk-ant-api03-your-key-here

# The system uses Claude as a fallback when Ollama is unavailable
# or for complex tasks that require stronger reasoning.
# Recommended model: claude-sonnet-4-20250514`;

  const intentSystemPrompt = `// INTENT_SYSTEM_PROMPT used for message classification
const INTENT_SYSTEM_PROMPT = \`You are an intent classifier for a commerce platform.
Classify the user's message into one of these intents:

INTENTS:
- create_deal: User wants to create a discount or promotion
- check_inventory: User wants to check stock levels
- update_price: User wants to change a product price
- view_analytics: User wants to see sales/performance data
- manage_agent: User wants to configure AI agent settings
- approve_action: User wants to approve a pending agent action
- reject_action: User wants to reject a pending agent action
- general_query: General question about the platform
- unknown: Cannot determine intent

Respond with ONLY the intent name, nothing else.\`;

// Usage with Ollama
const classifyIntent = async (message) => {
  const response = await fetch('http://localhost:11434/api/generate', {
    method: 'POST',
    body: JSON.stringify({
      model: 'mistral:7b',
      prompt: \`\${INTENT_SYSTEM_PROMPT}\\n\\nUser: \${message}\\nIntent:\`,
      stream: false
    })
  });
  const data = await response.json();
  return data.response.trim().toLowerCase();
};`;

  const fallbackLogic = `// Auto-fallback: Ollama → Claude API
const generateResponse = async (prompt, options = {}) => {
  // Try Ollama first (free, local)
  try {
    const ollamaHealth = await fetch('http://localhost:11434/api/tags');
    if (ollamaHealth.ok) {
      const response = await fetch('http://localhost:11434/api/generate', {
        method: 'POST',
        body: JSON.stringify({
          model: options.model || 'mistral:7b',
          prompt,
          stream: false
        })
      });
      if (response.ok) {
        const data = await response.json();
        return { text: data.response, provider: 'ollama' };
      }
    }
  } catch (err) {
    console.warn('Ollama unavailable, falling back to Claude API');
  }

  // Fallback to Claude API
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.ANTHROPIC_API_KEY,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1024,
      messages: [{ role: 'user', content: prompt }]
    })
  });
  const data = await response.json();
  return { text: data.content[0].text, provider: 'claude' };
};`;

  const healthEndpoint = `// LLM Health Check endpoint
// GET /v1/messaging/llm/health

curl http://localhost:3000/v1/messaging/llm/health

// Response:
{
  "status": "ok",
  "ollama": {
    "status": "connected",
    "url": "http://localhost:11434",
    "models": ["mistral:7b", "llama3:8b"],
    "gpu_available": true
  },
  "claude": {
    "status": "configured",
    "model": "claude-sonnet-4-20250514",
    "api_key_set": true
  },
  "active_provider": "ollama",
  "fallback_enabled": true
}`;

  const models = [
    {
      model: 'mistral:7b',
      size: '~4 GB',
      useCase: 'Intent classification, quick responses',
      speed: 'Fast (~50 tok/s on M2)',
      recommended: true,
    },
    {
      model: 'llama3:8b',
      size: '~4.7 GB',
      useCase: 'Text generation, complex reasoning',
      speed: 'Medium (~35 tok/s on M2)',
      recommended: true,
    },
    {
      model: 'llama3:70b',
      size: '~40 GB',
      useCase: 'Advanced reasoning (requires 64GB+ RAM)',
      speed: 'Slow (~5 tok/s on M4 Pro)',
      recommended: false,
    },
    {
      model: 'phi3:3.8b',
      size: '~2.3 GB',
      useCase: 'Lightweight tasks, low-memory devices',
      speed: 'Very fast (~80 tok/s on M2)',
      recommended: false,
    },
  ];

  const costComparison = [
    { provider: 'Ollama (Local)', monthlyCost: '$0', latency: '~100-300ms', privacy: 'Full (on-device)', limits: 'Hardware only' },
    { provider: 'Claude API', monthlyCost: '~$5-15', latency: '~500-2000ms', privacy: 'Anthropic servers', limits: 'Rate limits apply' },
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Infrastructure
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Local LLM Setup
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Run AI models locally with Ollama for zero-cost inference, with automatic
          fallback to Claude API when needed.
        </p>
      </div>

      {/* Ollama Setup */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Server className="w-6 h-6 text-[#3EF1C7]" />
          Ollama Installation
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Ollama runs LLMs locally on your Mac Mini. Install it and pull the recommended models:
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(ollamaInstall, 'install')}
          >
            {copiedCode === 'install' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{ollamaInstall}</code>
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Health Check</h3>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(ollamaHealthCheck, 'ollamahealth')}
          >
            {copiedCode === 'ollamahealth' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{ollamaHealthCheck}</code>
          </pre>
        </div>
      </section>

      {/* Claude API Setup */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-[#3EF1C7]" />
          Claude API Setup
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Get your API key from{' '}
          <a href="https://console.anthropic.com" className="text-[#3EF1C7] hover:underline" target="_blank" rel="noopener noreferrer">
            console.anthropic.com
          </a>{' '}
          and add it to your environment. Claude serves as the fallback LLM when Ollama is unavailable.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(claudeSetup, 'claude')}
          >
            {copiedCode === 'claude' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{claudeSetup}</code>
          </pre>
        </div>
      </section>

      {/* Auto-Fallback */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-6 h-6 text-[#3EF1C7]" />
          Auto-Fallback System
        </h2>
        <p className="text-[#94A3B8] mb-4">
          If Ollama fails or is unavailable, the system automatically falls back to Claude API.
          This ensures your agents are always responsive, even during Ollama maintenance or high load.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(fallbackLogic, 'fallback')}
          >
            {copiedCode === 'fallback' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{fallbackLogic}</code>
          </pre>
        </div>
      </section>

      {/* Intent Classification */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Intent Classification System Prompt
        </h2>
        <p className="text-[#94A3B8] mb-4">
          The INTENT_SYSTEM_PROMPT is used to classify incoming messages into actionable intents.
          This is the core of the messaging-to-action pipeline:
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(intentSystemPrompt, 'intent')}
          >
            {copiedCode === 'intent' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{intentSystemPrompt}</code>
          </pre>
        </div>
      </section>

      {/* Model Selection Guide */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Model Selection Guide
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Model</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Size</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Use Case</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Speed</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3">
                      <span className="text-[#E2E8F0]">{m.model}</span>
                      {m.recommended && (
                        <Badge className="ml-2 bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 text-xs">
                          Recommended
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-[#94A3B8]">{m.size}</td>
                    <td className="p-3 text-[#94A3B8]">{m.useCase}</td>
                    <td className="p-3 text-[#94A3B8]">{m.speed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Cost Comparison */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-[#3EF1C7]" />
          Cost Comparison
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Provider</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Monthly Cost</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Latency</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Privacy</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Limits</th>
                </tr>
              </thead>
              <tbody>
                {costComparison.map((row, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3 text-[#E2E8F0] font-medium">{row.provider}</td>
                    <td className="p-3 text-[#3EF1C7] font-medium">{row.monthlyCost}</td>
                    <td className="p-3 text-[#94A3B8]">{row.latency}</td>
                    <td className="p-3 text-[#94A3B8]">{row.privacy}</td>
                    <td className="p-3 text-[#94A3B8]">{row.limits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Health Check Endpoint */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-6 h-6 text-[#3EF1C7]" />
          Health Check Endpoint
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Monitor the LLM subsystem health via the dedicated health endpoint:
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(healthEndpoint, 'healthep')}
          >
            {copiedCode === 'healthep' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{healthEndpoint}</code>
          </pre>
        </div>
      </section>

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/mac-mini">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Mac Mini Setup <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Complete hardware and infrastructure setup guide.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/agent-admin">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Agent Admin <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure and manage AI agents that use the LLM.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
